// src/components/Dashboard.js

import React, { useEffect, useState, useMemo } from 'react';
import api from '../api';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardActionArea,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  ListAlt,
  Event as EventIcon,
  ArrowForward,
  Add,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import dayjs from 'dayjs';
import LoadingState from './LoadingState';
import { auth } from '../firebase';

const ensureArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }
  if (!value || value === null) {
    return [];
  }
  return [value];
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

function Dashboard() {
  const [events, setEvents] = useState([]);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(null); // 'list' | 'event' | null
  const [newName, setNewName] = useState('');
  const [newEventDate, setNewEventDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [creating, setCreating] = useState(false);

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const user = auth.currentUser;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [eventsRes, listsRes] = await Promise.all([
          api.get('/events'),
          api.get('/lists/recent'),
        ]);

        setEvents(ensureArray(eventsRes.data));
        setLists(ensureArray(listsRes.data));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const recentLists = useMemo(() => {
    return [...lists]
      .map((list) => ({
        ...list,
        recentAt:
          list.lastViewedAt ||
          list.recentAt ||
          list.lastActivityAt ||
          list.updatedAt ||
          list.createdAt,
      }))
      .sort((a, b) => new Date(b.recentAt) - new Date(a.recentAt))
      .slice(0, 4);
  }, [lists]);

  const recentEvents = useMemo(() => {
    return [...events].slice(0, 4);
  }, [events]);

  const openCreateDialog = (kind) => {
    setNewName('');
    setNewEventDate(dayjs().format('YYYY-MM-DD'));
    setCreateDialog(kind);
  };

  const closeCreateDialog = () => {
    if (!creating) setCreateDialog(null);
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name || creating) return;
    const isList = createDialog === 'list';
    setCreating(true);
    try {
      const response = isList
        ? await api.post('/lists', { name })
        : await api.post('/events', { name, date: newEventDate });
      setCreateDialog(null);
      navigate(`/${isList ? 'lists' : 'events'}/${response.data._id}`);
    } catch (error) {
      console.error(`Error creating ${isList ? 'list' : 'event'}:`, error);
      enqueueSnackbar(`Failed to create ${isList ? 'list' : 'event'}.`, { variant: 'error' });
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <LoadingState message="Loading your dashboard..." minHeight="80vh" />
    );
  }

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 0 } }}>
      <Box mb={4}>
        <Typography
          variant="h3"
          gutterBottom
          sx={{ fontFamily: '"Fraunces", Georgia, serif' }}
        >
          {getGreeting()}, {user?.displayName?.split(' ')[0] || 'User'}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Here's what's happening with your lists and events.
        </Typography>
      </Box>

      <Grid container spacing={{ xs: 2, md: 4 }}>
        {/* Recent Lists */}
        <Grid item xs={12} md={6}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <ListAlt sx={{ color: 'primary.main' }} />
              <Typography variant="h6" fontWeight="bold">
                Recent Lists
              </Typography>
            </Box>
            <Button endIcon={<ArrowForward />} onClick={() => navigate('/lists')}>
              View All
            </Button>
          </Box>
          {recentLists.length === 0 ? (
            <Paper
              sx={{
                p: 4,
                textAlign: 'center',
                color: 'text.secondary',
                border: '2px dashed',
                borderColor: 'divider',
                backgroundColor: 'transparent',
                boxShadow: 'none',
              }}
            >
              <ListAlt sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
              <Typography>No lists yet. Create one to get started!</Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {recentLists.map((list) => (
                <Grid item xs={12} key={list._id}>
                  <Card
                    elevation={1}
                    sx={{
                      '&:hover': { transform: 'translateY(-2px)', transition: '0.2s' },
                    }}
                  >
                    <CardActionArea onClick={() => navigate(`/lists/${list._id}`)} sx={{ p: 2 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography
                          variant="subtitle1"
                          fontWeight="medium"
                          sx={{ wordBreak: 'break-word', pr: 1 }}
                        >
                          {list.name}
                        </Typography>
                        <ListAlt sx={{ color: 'primary.main' }} fontSize="small" />
                      </Box>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
          <Button
            fullWidth
            variant="outlined"
            startIcon={<Add />}
            onClick={() => openCreateDialog('list')}
            sx={{ mt: 2 }}
          >
            New list
          </Button>
        </Grid>

        {/* Recent Events */}
        <Grid item xs={12} md={6}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <EventIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h6" fontWeight="bold">
                Your Events
              </Typography>
            </Box>
            <Button endIcon={<ArrowForward />} onClick={() => navigate('/events')}>
              View All
            </Button>
          </Box>
          {recentEvents.length === 0 ? (
            <Paper
              sx={{
                p: 4,
                textAlign: 'center',
                color: 'text.secondary',
                border: '2px dashed',
                borderColor: 'divider',
                backgroundColor: 'transparent',
                boxShadow: 'none',
              }}
            >
              <EventIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
              <Typography>No events yet. Join or create one!</Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {recentEvents.map((event) => (
                <Grid item xs={12} key={event._id}>
                  <Card
                    elevation={1}
                    sx={{
                      '&:hover': { transform: 'translateY(-2px)', transition: '0.2s' },
                    }}
                  >
                    <CardActionArea onClick={() => navigate(`/events/${event._id}`)} sx={{ p: 2 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle1" fontWeight="medium">
                          {event.name}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="caption" color="text.secondary">
                            {event.members?.length || 0} members
                          </Typography>
                          <EventIcon sx={{ color: 'primary.main' }} fontSize="small" />
                        </Box>
                      </Box>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
          <Button
            fullWidth
            variant="outlined"
            startIcon={<Add />}
            onClick={() => openCreateDialog('event')}
            sx={{ mt: 2 }}
          >
            New event
          </Button>
        </Grid>
      </Grid>

      {/* Create list / event dialog */}
      <Dialog open={createDialog !== null} onClose={closeCreateDialog} fullWidth maxWidth="xs">
        <DialogTitle>{createDialog === 'event' ? 'Create New Event' : 'Create New List'}</DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleCreate();
          }}
        >
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label={createDialog === 'event' ? 'Event Name' : 'List Name'}
              type="text"
              fullWidth
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            {createDialog === 'event' && (
              <TextField
                margin="dense"
                label="Event Date"
                type="date"
                fullWidth
                value={newEventDate}
                InputLabelProps={{ shrink: true }}
                onChange={(e) => setNewEventDate(e.target.value)}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeCreateDialog} disabled={creating}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={!newName.trim() || creating}>
              Create
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default Dashboard;
